import {
  memo,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  CHART,
  CHART_DOTS,
  CHART_DOT_WAVE,
  chartDotGrid,
  chartDotRows,
  type ChartZone,
} from './chartGeometry';

export const ChartAtmosphere = memo(function ChartAtmosphere({
  zone,
}: {
  zone: ChartZone;
}) {
  const id = useId();
  const band = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(
    chartDotGrid(
      CHART.width - 2 - 2 * CHART.inset - 2 * CHART_DOTS.inset,
      CHART_DOT_WAVE.scale,
    ).width,
  );
  useLayoutEffect(() => {
    const node = band.current!;
    const measure = (available: number) =>
      setWidth(
        chartDotGrid(available - 2 * CHART_DOTS.inset, CHART_DOT_WAVE.scale)
          .width,
      );
    measure(Number.parseFloat(getComputedStyle(node).width));
    const observer = new ResizeObserver(([entry]) =>
      measure(entry.contentRect.width),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return (
    <>
      {(['up', 'down'] as const).map((side) => {
        const pattern = id + '-dots-' + side;
        const rows = chartDotRows(side);
        const dot = (
          <image
            href={
              '/images/chart-dot-' + (side === 'up' ? 'run' : 'die') + '.svg'
            }
            x={((CHART_DOT_WAVE.scale - 1) * CHART_DOTS.size) / 2}
            y={(CHART_DOTS.step - CHART_DOTS.size) / 2}
            width={CHART_DOTS.size}
            height={CHART_DOTS.size}
          />
        );
        return (
          <span
            key={side}
            ref={side === 'up' ? band : undefined}
            className={
              'chart-band is-' + side + (zone === side ? ' is-active' : '')
            }
            aria-hidden="true"
          >
            <svg
              className="chart-dots"
              width={width}
              height={CHART_DOTS.step * CHART_DOTS.rows}
              focusable="false"
              style={
                {
                  '--dot-wave-ms': CHART_DOT_WAVE.ms + 'ms',
                  '--dot-wave-scale': CHART_DOT_WAVE.scale,
                } as CSSProperties
              }
            >
              <defs>
                <pattern
                  id={pattern}
                  patternUnits="userSpaceOnUse"
                  width={CHART_DOTS.step}
                  height={CHART_DOTS.step}
                >
                  {dot}
                </pattern>
                {rows.map((row) => (
                  <pattern
                    key={row.order}
                    id={pattern + '-wave-' + row.order}
                    patternUnits="userSpaceOnUse"
                    width={CHART_DOTS.step}
                    height={CHART_DOTS.step}
                  >
                    {/* Animate one dot per pattern, never scale the whole row.
                        The 6px growth allowance keeps the outside dots intact. */}
                    <g
                      className="chart-dot-wave"
                      style={{ animationDelay: row.delay + 'ms' }}
                    >
                      {dot}
                    </g>
                  </pattern>
                ))}
              </defs>
              <rect
                className="chart-dots-rest"
                width="100%"
                height="100%"
                fill={`url(#${pattern})`}
              />
              <g className="chart-dots-motion">
                {rows.map((row) => (
                  <rect
                    key={row.order}
                    data-wave-row={row.order}
                    x="0"
                    y={row.y}
                    width="100%"
                    height={CHART_DOTS.step}
                    fill={`url(#${pattern}-wave-${row.order})`}
                  />
                ))}
              </g>
            </svg>
          </span>
        );
      })}
    </>
  );
});
